import { useEffect, useMemo, useState } from 'react';
import { useT } from '../../i18n';
import { Combobox, Option, Field, Badge } from '@fluentui/react-components';

/** Etiqueta opcional al lado de la opción (p. ej. cultivo · ciclo de una zona). */
export interface SearchableSelectTag {
  text: string;
  tone: 'success' | 'warning' | 'danger' | 'neutral';
}

export interface SearchableSelectOption {
  id: string;
  label: string;
  /** Nivel de árbol (0 = raíz): la lista lo muestra sangrado con `name`; el campo sigue mostrando `label`. */
  depth?: number;
  name?: string;
  tag?: SearchableSelectTag;
}

const TAG_COLOR = { success: 'success', warning: 'warning', danger: 'danger', neutral: 'informative' } as const;

export interface SearchableSelectProps {
  label?: string;
  placeholder?: string;
  options: SearchableSelectOption[];
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  noResultsText?: string;
  /** Mensaje de error (campo obligatorio vacío): pinta el control en rojo y lo muestra debajo. */
  error?: string;
}

/**
 * Dropdown con filtro por texto (M-2): reemplaza los `<Input>` de texto
 * libre para loteId/ranchoId/cultivo/protocolo/producto por una lista
 * buscable — el usuario ya no tiene que saber ni teclear el id exacto.
 */
export function SearchableSelect({
  label, placeholder, options, value, onChange, disabled, noResultsText, error,
}: SearchableSelectProps) {
  const t = useT();
  const noResults = noResultsText ?? t.common.searchNoResults;
  const selected = useMemo(() => options.find(o => o.id === value), [options, value]);
  const [query, setQuery] = useState(selected?.label ?? '');

  // Si el value viene de afuera (ej. reset de formulario), sincroniza el texto mostrado.
  useEffect(() => {
    setQuery(selected?.label ?? '');
  }, [selected]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || q === selected?.label.toLowerCase()) return options;
    return options.filter(o => o.label.toLowerCase().includes(q));
  }, [options, query, selected]);

  const combobox = (
    <Combobox
      placeholder={placeholder}
      value={query}
      selectedOptions={value ? [value] : []}
      onOptionSelect={(_, data) => {
        onChange((data.optionValue as string) ?? '');
        setQuery(data.optionText ?? '');
      }}
      onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
      disabled={disabled}
      // Fluent fija min-width 250px: en columnas angostas (grid de 2) el chevron
      // se salía del recuadro. Ocupa el ancho disponible sin desbordar.
      style={{ minWidth: 0, width: '100%' }}
    >
      {filtered.length === 0
        ? <Option key="__empty" value="" disabled>{noResults}</Option>
        : filtered.map(o => (
          <Option key={o.id} value={o.id} text={o.label}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', width: '100%' }}>
              <span style={o.depth ? { paddingLeft: (o.depth - 1) * 16 } : undefined}>{o.depth ? '↳ ' : ''}{o.name ?? o.label}</span>
              {o.tag && <Badge appearance="tint" size="small" color={TAG_COLOR[o.tag.tone]}>{o.tag.text}</Badge>}
            </span>
          </Option>
        ))}
    </Combobox>
  );

  const errProps = error ? { validationState: 'error' as const, validationMessage: error } : {};
  return label || error ? <Field label={label} {...errProps}>{combobox}</Field> : combobox;
}
